/*
  THE SINGLE SWITCH between mock data and the real control plane.

  Every endpoint module in src/lib/api/endpoints/ asks `isMockTransport()` once and then
  calls either the mock handler or the HTTP client. Nothing else in the application knows
  that mocks exist: no component imports from src/mocks/, and no query key changes. To cut
  over to the real API, set VITE_API_TRANSPORT=http (or just remove the mock default once
  jit-api is up) and delete src/mocks/. Nothing else has to move.

  Default is mock, because Phase 1 ships the frontend ahead of the backend and a console
  that silently fails against an absent server is worse than one that is clearly on
  fixtures. The header banner in the app shell says which transport is live, so nobody
  demos fixtures believing they are real.
*/

export type ApiTransport = 'mock' | 'http';

function readTransport(): ApiTransport {
  const raw = import.meta.env.VITE_API_TRANSPORT;
  return raw === 'http' ? 'http' : 'mock';
}

export const API_TRANSPORT: ApiTransport = readTransport();

export function isMockTransport(): boolean {
  return API_TRANSPORT === 'mock';
}

/**
 * Base path, not a base URL. The console is served from the same origin as the API through
 * the edge proxy, which is what makes the HttpOnly refresh cookie first-party and lets it
 * be SameSite=Strict. A cross-origin API base would force SameSite=None and give up that
 * protection, so this is a relative path on purpose.
 */
export const API_BASE_PATH = '/api/v1';

/** Artificial latency for the mock transport, so loading states are exercised. */
export const MOCK_LATENCY_MS = 220;
